import type { IRoleRequirement } from './RoleRequirement';
import {
  SORT_RELEVANCE,
  SORT_SEARCH_RELEVANCE,
  SORT_ANSWERS_MATCHING,
  SORT_OPTIONS,
} from './../../constants/sortOrder';
import { set, toJS } from 'mobx';
import {
  flow,
  getRoot,
  type IAnyModelType,
  type Instance,
  types,
} from 'mobx-state-tree';
import JobApplication, { type IJobApplication } from './JobApplication';
import BucketStatsStore from '../BucketStatsStore';
import { STATE_ERROR, STATE_LOADED, STATE_LOADING, stateType } from '../state';
import { getJobApplicationList } from './utils/getJobApplications';
import {
  trackClearSelectedProspectsClicked,
  trackSelectAllProspectsClicked,
  trackViewApplicationsAfterBulkMovePressed,
} from '../../utils/events';

import type { IStore } from '../Store';
import type { IUserStore } from '../UserStore';
import type { IJobStore } from '../JobStore';
import type { OVERLAY_MODAL } from '../../constants/modals';
import {
  type Bucket,
  BUCKETS,
  SelectAllStatus,
  type SearchFilters,
} from './types';
// @ts-ignore
import pageLoadOnUserChange from '../reactions/pageLoadOnUserChange';
import { updateStatusForSelectedJobApplications } from './utils/updateStatusForJobApplications';
import { logError } from '../../utils/logError';
import { BULK_MOVE_LIMIT, PER_PAGE } from '../../constants/numPerPage';
import { sendEmailForSelectedApplications } from './utils/emailAction';
import { sendDetailView } from '../../services/jobApplicationService/sendDetailView';
import type { QueryParams } from '../../utils/hooks/types';
import JobApplicationUiStore from './JobApplicationUiStore';
import {
  updateApplicationList,
  updateOverlay,
  updatePage,
  updateSelectedDetails,
} from './onQueryParamsChanged';
import jobApplicationService from '../../services/jobApplicationService';
import {
  type CertsyOrListEventCaptureInfo,
  getCertsyOrListEventCaptureInfo,
} from './getEventCaptureInfos';
import { handleTopUpCache } from './utils/handleTopUpCache';
import FiltersStore from './FiltersStore/FiltersStore';
import type { IQuestionnaireStore } from '../JobStore/QuestionnaireStore';
import scrollToStartOfCandidateList from './utils/scrollToStartOfCandidateList';
import type { JOB_APPLICATION_TAB } from '../../constants/tabs';
import { getDetailedJobApplicationFromHirerGraph } from './utils/getDetailedJobApplication';
import getCountsForFilters from './utils/getCountsForFilters';
import type { BucketStatusCounts } from '../../graphql/fetchProspectsWithPaginationByJobId';
import type { Language } from '@seek/melways-sites';
import type { FiltersEventCaptureData } from 'src/services/jobApplicationService/buildCaptureEventData';
import type { TrackingExperimentsAttributes } from 'src/utils/eventsTypes';
import { UserLoadState } from '../UserStore/types';

export interface LoadJobApplicationsParams {
  language: Language;
  bucket?: Bucket;
  searchText?: string;
  sortBy?: string;
  page?: number;
  filters: SearchFilters;
  experiments?: TrackingExperimentsAttributes[];
}

const UndoState = types.model('UndoState', {
  previousBucket: types.enumeration<BUCKETS | ''>(
    'Buckets',
    Object.values(BUCKETS),
  ),
  destinationBucket: types.enumeration<BUCKETS | ''>(
    'Buckets',
    Object.values(BUCKETS),
  ),
  pageNumber: types.number,
  position: types.number,
  jobApplication: types.maybe(
    types.late((): IAnyModelType => types.reference(JobApplication) as any),
  ),
});

const JobApplicationStore = types
  .model('JobApplicationStore', {
    bucket: types.optional(
      types.enumeration<BUCKETS>('Buckets', Object.values(BUCKETS)),
      BUCKETS.INBOX,
    ),
    preFilterTotal: types.optional(types.number, 0),
    total: types.optional(types.number, 0),
    searchText: types.optional(types.string, ''),
    lastResultSearchText: types.optional(types.string, ''),
    /**
     * loadedJobApplications
     *
     * @description All applications that was fetched and loaded into the Store
     *
     * This is an Observable map which has the ability to normalise and dedupe objects using the object's types.identifier field.
     * This means we can use `.put` to add records to the map: if an object with given identifier does not exist, it automatically dedupe/update/normalise the record
     * https://mobx-state-tree.js.org/API/#map
     */
    loadedJobApplications: types.map(
      types.late((): IAnyModelType => JobApplication),
    ),
    /**
     * jobApplications
     *
     * @description Job applications on the current page.
     */
    jobApplications: types.array(
      types.late((): IAnyModelType => types.reference(JobApplication) as any),
    ),
    currentPage: types.optional(types.number, 1),
    /**
     * independentDetailedJobApplication
     *
     * @description Job application to be shown in non-"Candidate list" tabs e.g. Notes tab
     */
    independentDetailedJobApplication: types.maybeNull(
      types.late((): IAnyModelType => types.reference(JobApplication) as any),
    ),
    shownDetailsJobApplication: types.maybeNull(
      types.reference(
        types.late((): IAnyModelType => JobApplication),
        {
          get(identifier, parent) {
            const p = parent as any;
            return (
              p?.getJobApplicationById(identifier) ??
              (p?.independentDetailedJobApplication?.id === identifier
                ? p?.independentDetailedJobApplication
                : null)
            );
          },
          set(value) {
            return value?.id;
          },
        },
      ),
    ),
    bucketStatsStore: types.optional(BucketStatsStore, {}),
    sortBy: types.optional(
      types.enumeration(
        'SortBy',
        SORT_OPTIONS.map((option) => option.value),
      ),
      SORT_ANSWERS_MATCHING.value,
    ),
    state: stateType,
    selectAllClicked: types.optional(types.boolean, false),
    uiState: types.optional(JobApplicationUiStore, {}),
    undoState: types.maybe(UndoState),
    cachePending: types.optional(types.boolean, false),
    filtersStore: types.optional(FiltersStore, {}),
  })
  .views((self) => ({
    get rootStore(): IStore {
      return getRoot(self);
    },

    get userStore(): IUserStore {
      return (getRoot(self) as IStore).userStore;
    },
    get jobStore(): IJobStore {
      return (getRoot(self) as IStore).jobStore;
    },

    get isLoading() {
      return self.state === STATE_LOADING;
    },

    get isLoaded() {
      return self.state === STATE_LOADED;
    },

    get isError() {
      return self.state === STATE_ERROR || self.bucketStatsStore.isError;
    },

    get initialTotalOfJobApplications() {
      return self.total;
    },

    get notRemovedJobApplications(): IJobApplication[] {
      return self.jobApplications.filter(
        (jobApplication) => !jobApplication.isMarkedAsRemoved,
      );
    },

    get removedJobApplications(): IJobApplication[] {
      return self.jobApplications.filter(
        (jobApplication) => jobApplication.isMarkedAsRemoved,
      );
    },
  }))
  .views((self) => ({
    get currentPreFilterTotalOfJobApplications() {
      return self.preFilterTotal - self.removedJobApplications.length;
    },

    get currentTotalOfJobApplications() {
      return self.total - self.removedJobApplications.length;
    },

    get selectedJobApplicationsWithDetails(): IJobApplication[] {
      return self.notRemovedJobApplications.filter(
        (jobApplication) => jobApplication.selected,
      );
    },
  }))
  .views((self) => ({
    get numberOfPages() {
      return Math.ceil(self.currentTotalOfJobApplications / PER_PAGE);
    },

    get currentPageJobApplications() {
      return self.notRemovedJobApplications.slice(0, PER_PAGE);
    },

    get hasRRErrorOnCurrentPage(): boolean {
      return this.currentPageJobApplications.some(
        (jobApp) => jobApp.hasRRError,
      );
    },

    /**
     * selectedJobApplications
     *
     * @description An array of selected Job Applications from the all loaded Job Applications
     */
    get selectedJobApplications(): IJobApplication[] {
      return Array.from(self.loadedJobApplications.values()).filter(
        (jobApp) => jobApp.selected && !jobApp.isMarkedAsRemoved,
      );
    },

    get selectedJobApplicationIds(): string[] {
      return this.selectedJobApplications.map((jobApp) => jobApp.id);
    },

    get selectedJobApplicationUpToBulkMoveLimit(): IJobApplication[] {
      return this.selectedJobApplications.slice(0, BULK_MOVE_LIMIT);
    },
  }))
  .views((self) => ({
    get selectedCount() {
      return self.selectedJobApplications.length;
    },

    get selectAllStatus(): SelectAllStatus {
      if (self.notRemovedJobApplications.length === 0) {
        return SelectAllStatus.Empty;
      }

      const currentPageJobApplications = self.currentPageJobApplications;

      const selectedJobApplicationsOnCurrentPageCount =
        currentPageJobApplications.filter((jobApp) => jobApp.selected).length;

      if (selectedJobApplicationsOnCurrentPageCount === 0) {
        return SelectAllStatus.NonSelected;
      }

      if (self.currentTotalOfJobApplications === this.selectedCount) {
        return SelectAllStatus.SelectAllInFolder;
      }

      if (
        selectedJobApplicationsOnCurrentPageCount ===
        currentPageJobApplications.length
      ) {
        if (self.selectAllClicked) {
          return SelectAllStatus.SelectAll;
        }
        return SelectAllStatus.SelectAllIndividually;
      }

      return SelectAllStatus.PartialSelect;
    },

    get hasSearchText() {
      return self.lastResultSearchText !== '';
    },

    get positionId() {
      return self.rootStore.positionId;
    },

    get jobId() {
      return self.rootStore.jobId;
    },

    get bucketStats() {
      return self.bucketStatsStore.bucketStats;
    },

    get newCount() {
      return self.bucketStatsStore.bucketStats.newCount;
    },

    get certsyEventCaptureInfo(): CertsyOrListEventCaptureInfo {
      return getCertsyOrListEventCaptureInfo(self as IJobApplicationStore);
    },

    get isInBucketMode(): boolean {
      return !this.hasSearchText;
    },
  }))
  .views((self) => ({
    /**
     * bulkActionEventAttributes
     *
     * @description This is used for bulk actions if bulk move limit is not considered
     */
    get bulkActionEventAttributes() {
      const bulkActionCount = self.selectedCount;
      const objectInteraction = self.selectAllStatus;
      return {
        objectInteraction,
        bulkActionCount,
      };
    },
  }))
  .actions(() => ({
    // allows store updates during async operations
    runInAction(fn: (args?: any[]) => any) {
      return fn();
    },
  }))
  .actions((self) => {
    const putToLoadedJobApplications = (jobApp: IJobApplication): void => {
      self.loadedJobApplications.put(jobApp);
    };

    const incrementTotal = (count: number = 1) => {
      self.total += count;
      self.preFilterTotal += count;
    };

    const hideDetails = () => {
      self.shownDetailsJobApplication = null;
    };

    const appendJobApplications = (jobApplications: IJobApplication[]) => {
      set(self, {
        jobApplications: self.jobApplications.concat(
          jobApplications,
        ) as IJobApplication[],
      });
    };

    const setCachedPending = (cachePending: boolean) => {
      set(self, {
        cachePending,
      });
    };

    const updateSearchText = (newSearchText: string) => {
      self.searchText = newSearchText;
    };

    const getJobApplicationById = (id: string): IJobApplication | undefined => {
      const jobApplications = self.jobApplications;
      return jobApplications.find(
        (jobApplication: IJobApplication) => jobApplication.id === id,
      );
    };

    const showDetails: (params: {
      id: string;
      language: Language;
      initialLoad: boolean;
      filtersEventCaptureData: FiltersEventCaptureData;
      tab?: JOB_APPLICATION_TAB;
    }) => Promise<void> = flow(function* ({
      id,
      language,
      initialLoad,
      filtersEventCaptureData,
      tab,
    }) {
      try {
        let jobApplication = getJobApplicationById(id);
        if (!jobApplication && !initialLoad) {
          const result = yield getDetailedJobApplicationFromHirerGraph(
            id,
            self.jobId,
            language,
            self.jobStore.job.jobInformation.jobCountry,
          );

          if (result === 'UNAUTHENTICATED') {
            self.rootStore.userStore.setUserLoadState(
              UserLoadState.UNAUTHENTICATED,
            );
            return;
          }
          jobApplication = result;
          self.loadedJobApplications.put(jobApplication);
          self.independentDetailedJobApplication = jobApplication;
        }
        if (jobApplication) {
          if (tab) {
            jobApplication.selectTabInDetailView(tab);
          }
          self.shownDetailsJobApplication = jobApplication;

          // FIXME: Checking whether it's the initial load to call `sendDetailView` is a hacky way to avoid sending the event multiple times
          // This is because the app is triggering `showDetails` again after the initial load
          // However, there's no indication that `showDetails` will be called again after the initial load.
          if (!initialLoad) {
            sendDetailView({
              store: self.rootStore,
              jobApplication,
              filters: filtersEventCaptureData,
            });
          }

          jobApplication.markRead({
            filtersEventCaptureData,
          });
        }
      } catch (error: any) {
        logError(
          {
            errorCode: 'SHOW_JOB_APPLICATION_DETAILS',
            description: 'Error fetching job application details',
          },
          {
            error,
          },
        );
      }
    });

    const setDefaultSort = (sortBy: string) => {
      self.sortBy = sortBy;
    };

    const updateBucketCount = (
      originalBucket: Bucket,
      newbucket: Bucket,
      count = 1,
    ) => {
      self.bucketStats.moveBucket(originalBucket, newbucket, count);
    };

    const updateBucketStats = (bucketStats: BucketStatusCounts) => {
      self.bucketStatsStore = BucketStatsStore.create({
        state: STATE_LOADED,
        bucketStats,
      });
    };

    const updateFiltersCount = (
      jobApplication: IJobApplication,
      mode: 'AddJobApp' | 'RemoveJobApp',
    ): undefined => {
      if (self.filtersStore.questions.length <= 0 || self.hasSearchText) {
        return;
      }
      const changeAmount = mode === 'AddJobApp' ? 1 : -1;

      jobApplication.roleRequirements.forEach(
        (roleRequirement: IRoleRequirement) => {
          roleRequirement.rrAnswers.forEach((rrAnswer) => {
            if (rrAnswer.globalIdOrId) {
              self.filtersStore.updateRRAnswerCount(
                rrAnswer.globalIdOrId,
                changeAmount,
              );
            }
          });
        },
      );
    };

    const updateSelectAllClickedState = (clicked: boolean) => {
      self.selectAllClicked = clicked;
    };

    const updateAllSelectedOnCurrentPage = (
      selected: boolean,
      clickSelectedAll = false,
    ): void => {
      self.currentPageJobApplications.forEach(
        (jobApplication: IJobApplication) => {
          jobApplication.updateSelected(selected);
        },
      );
      updateSelectAllClickedState(clickSelectedAll);
    };

    const clickSelectAllOnCurrentPage = (): void => {
      const updatedSelected =
        self.selectAllStatus === SelectAllStatus.NonSelected ||
        self.selectAllStatus === SelectAllStatus.PartialSelect;

      updateAllSelectedOnCurrentPage(updatedSelected, true);

      trackSelectAllProspectsClicked({
        objectInteraction: self.selectAllStatus,
        bulkActionCount: self.selectedCount,
      });
    };

    const clearAllSelected = (): void => {
      self.loadedJobApplications.forEach((jobApplication: IJobApplication) => {
        jobApplication.updateSelected(false);
      });
    };

    const clickClearAllSelected = ({
      tracking,
    }: {
      tracking: { actionOrigin: string };
    }): void => {
      clearAllSelected();
      trackClearSelectedProspectsClicked({
        bulkActionCount: self.selectedCount,
        actionOrigin: tracking.actionOrigin,
      });
    };

    const updateStatusForSelectedApplications: ({
      bucket,
      store,
      reloadJobApplications,
      filtersEventCaptureData,
    }: {
      bucket: Bucket;
      store: IStore;
      reloadJobApplications: () => Promise<void>;
      filtersEventCaptureData: FiltersEventCaptureData;
    }) => Promise<boolean> = flow(function* ({
      bucket,
      store,
      reloadJobApplications,
      filtersEventCaptureData,
    }) {
      try {
        return yield updateStatusForSelectedJobApplications({
          jobApplicationStore: self as IJobApplicationStore,
          store,
          bucket,
          reloadJobApplications,
          filtersEventCaptureData,
        });
      } catch (error: any) {
        logError(
          {
            errorCode: 'BULK_UPDATE_STATUS',
            description:
              'Error attempting: updateStatusForSelectedJobApplications',
          },
          {
            error,
          },
        );
        return false;
      }
    });

    const clearRemovedJobApplications = (): void => {
      set(self, {
        total: self.total - self.removedJobApplications.length,
        jobApplications: self.notRemovedJobApplications,
      });
    };

    const clearJobApplications = (): void => {
      set(self, { jobApplications: [] });
    };

    const sendProspectEmail = flow(function* ({
      subject,
      message,
      enableReplies,
      language,
    }: {
      subject: string;
      message: string;
      enableReplies: boolean;
      language: Language;
    }) {
      try {
        const result = yield sendEmailForSelectedApplications(
          self as IJobApplicationStore,
          subject,
          message,
          enableReplies,
          self.rootStore.getCandidateCardStateForTracking(),
          language,
          self.rootStore.jobStore.job.jobInformation.jobCountry,
        );
        if (result) {
          return true;
        }
        logError(
          {
            errorCode: 'SEND_EMAIL',
            description: 'sendBulkProspectEmail returned success: false',
          },
          {
            selectedJobApplicationIds: self.selectedJobApplicationIds,
          },
        );
        return false;
      } catch (error: any) {
        logError(
          {
            errorCode: 'SEND_EMAIL',
            description: 'Error attempting: sendEmailForSelectedApplications',
          },
          {
            error,
            selectedJobApplicationIds: self.selectedJobApplicationIds,
          },
        );
        return false;
      }
    });

    return {
      putToLoadedJobApplications,
      incrementTotal,
      showDetails,
      hideDetails,
      getJobApplicationById,
      setDefaultSort,
      updateBucketCount,
      updateBucketStats,
      updateFiltersCount,
      updateAllSelectedOnCurrentPage,
      clickSelectAllOnCurrentPage,
      clearAllSelected,
      clickClearAllSelected,
      updateStatusForSelectedApplications,
      sendProspectEmail,
      updateSelectAllClickedState,
      updateSearchText,
      clearJobApplications,
      clearRemovedJobApplications,
      appendJobApplications,
      setCachedPending,
    };
  })
  .actions((self) => {
    const topUpCandidateListWithCachedJobApplications = async ({
      language,
      filters,
    }: {
      language: Language;
      filters: SearchFilters;
    }) => {
      await handleTopUpCache({
        jobApplicationStore: self as IJobApplicationStore,
        language,
        displayCountry: self.jobStore.job.jobInformation.jobCountry,
        filters,
      });
    };

    const updateCountsForFilters = async ({
      language,
      filters,
    }: {
      language: Language;
      filters: SearchFilters;
    }) => {
      const filterCounts = await getCountsForFilters(
        self.jobId,
        self.searchText,
        language,
        self.jobStore.job.jobInformation.jobCountry,
        self.bucket,
        self.sortBy,
        self.currentPage,
        filters,
      );
      self.filtersStore.updateRRAnswerCounts(filterCounts?.rrFilterCounts);

      return filterCounts;
    };

    /**
     * loadAllJobApplications
     *
     * @description Function to load all job applications on the current settings: bucket, sort, search text and filters
     */
    const loadAllJobApplications = async ({
      selectAll,
      onCompleted,
      filters,
    }: {
      selectAll: boolean;
      onCompleted?: () => void;
      filters: SearchFilters;
    }): Promise<void> => {
      const bucket = self.bucket;
      const sortBy = self.sortBy;
      const searchText = self.searchText || '';

      set(self, {
        bucket,
        sortBy,
        searchText,
        lastResultSearchText: searchText,
      });
      (self as IJobApplicationStore).hideDetails();
      const sortByChecked = searchText ? SORT_SEARCH_RELEVANCE.value : sortBy;

      const allJobApplicationSummaries =
        await jobApplicationService.fetchAllJobApplications(
          searchText,
          bucket,
          sortByChecked,
          filters,
          self.jobId,
        );

      let allJobApplications: IJobApplication[] = [];
      if (allJobApplicationSummaries !== 'redirect') {
        allJobApplications = allJobApplicationSummaries.result.map(
          (jobApplication, index) =>
            JobApplication.create({
              id: jobApplication.adcentreProspectId,
              bucket, // Bucket does not come back from fetchAllJobApplications. However, we can assume that it's in the same bucket because we are querying using this bucket as input
              candidateId: '', // Summary job apps are not loaded with all details. But it's ok because we don't use 'candidateId' for jobs that are in the background.
              applicationCorrelationId: jobApplication.id,
              firstName: jobApplication.firstName,
              lastName: jobApplication.lastName,
              isRead: jobApplication.isRead,
              positionInList: index + 1,
              selected: selectAll
                ? true
                : self.selectedJobApplicationIds.includes(
                    jobApplication.adcentreProspectId,
                  ),
            }),
        );
      }

      self.runInAction(() => {
        // De-select all job apps already loaded (could be on the current page or previously loaded pages)
        self.loadedJobApplications.forEach(
          (jobApplication: IJobApplication) => {
            jobApplication.updateSelected(false);
          },
        );

        // Add all fetched job apps into loadedJobApplications if they are not on the same page
        allJobApplications.forEach((newJobApp) => {
          const existingLoadedJobApplication: IJobApplication =
            self.loadedJobApplications.get(newJobApp.id);

          if (existingLoadedJobApplication) {
            // newJobApp are summaries and only have some of the data.
            // We only want to update the client state here.
            // Otherwise, we will lose JobApplicationDetails fields
            existingLoadedJobApplication.selected = newJobApp.selected;
            existingLoadedJobApplication.positionInList =
              newJobApp.positionInList;
            self.putToLoadedJobApplications(existingLoadedJobApplication);
          } else {
            self.putToLoadedJobApplications(newJobApp);
          }
        });
      });

      if (onCompleted) {
        onCompleted();
      }
    };

    /**
     * loadJobApplications
     *
     * @description Function to load Job Applications of a page and the next page.
     * The next page Job Applications are used to fill up the main list of Job Applications in case one or many are moved to a different bucket.
     */
    const loadJobApplications = async (options: LoadJobApplicationsParams) => {
      pageLoadOnUserChange({
        store: self.rootStore,
        filters: options.filters,
        experiments: options.experiments,
      });
      const bucket = options.bucket || self.bucket;
      const sortBy = options.sortBy || self.sortBy;
      const searchText = options.searchText ?? (self.searchText || '');
      const page = options.page || self.currentPage;
      const language = options.language;
      const filters = options.filters;

      set(self, {
        bucket,
        sortBy,
        searchText,
        state: STATE_LOADING,
        currentPage: page,
        lastResultSearchText: searchText,
      });
      (self as IJobApplicationStore).hideDetails();
      const sortByChecked = searchText ? SORT_SEARCH_RELEVANCE.value : sortBy;

      const attributes = await getJobApplicationList(
        self.jobId,
        searchText,
        language,
        self.jobStore.job.jobInformation.jobCountry,
        self.bucket,
        sortByChecked,
        page,
        self.selectedJobApplicationIds,
        filters,
      );

      if (attributes === 'UNAUTHENTICATED') {
        self.rootStore.userStore.setUserLoadState(
          UserLoadState.UNAUTHENTICATED,
        );
        return;
      }

      self.runInAction(() => {
        // We must put all fetched load applications into loadedJobApplications to refer in other arrays
        attributes.jobApplications.forEach((newJobApp) => {
          self.putToLoadedJobApplications(newJobApp);
        });
        set(self, attributes);
      });

      scrollToStartOfCandidateList();

      self.filtersStore.updateRRAnswerCounts(
        attributes.filterCounts?.rrFilterCounts,
      );

      await handleTopUpCache({
        jobApplicationStore: self as IJobApplicationStore,
        language,
        displayCountry: self.jobStore.job.jobInformation.jobCountry,
        filters,
      });
      return attributes;
    };

    return {
      topUpCandidateListWithCachedJobApplications,
      loadJobApplications,
      loadAllJobApplications,
      updateCountsForFilters,
    };
  })
  .actions((self) => {
    const showOverlay = (overlay: OVERLAY_MODAL) => {
      self.rootStore.uiStore.modalStore.addModal(overlay);
    };

    const hideOverlay = () => {
      self.rootStore.uiStore.modalStore.clearOverlayModal();
    };

    const updateFromQuery: ({
      queryParams,
      language,
      initialLoad,
      filtersEventCaptureData,
      filters,
      experiments,
    }: {
      queryParams: QueryParams;
      language: Language;
      initialLoad: boolean;
      filtersEventCaptureData: FiltersEventCaptureData;
      filters: SearchFilters;
      experiments?: TrackingExperimentsAttributes[];
    }) => void = flow(function* ({
      queryParams,
      language,
      initialLoad,
      filtersEventCaptureData,
      filters,
      experiments,
    }) {
      self.uiState = JobApplicationUiStore.create(queryParams);
      self.searchText = queryParams.search || '';

      yield updateApplicationList(
        self as IJobApplicationStore,
        initialLoad,
        language,
        filters,
        experiments,
      );
      yield updatePage(
        self as IJobApplicationStore,
        language,
        filters,
        experiments,
      );
      yield updateOverlay(self as IJobApplicationStore);
      yield updateSelectedDetails(
        self as IJobApplicationStore,
        initialLoad,
        language,
        filtersEventCaptureData,
      );
    });

    const initLoad = async (
      positionId: number,
      queryParams: QueryParams,
      language: Language,
      experiments?: TrackingExperimentsAttributes[],
    ) => {
      self.setDefaultSort(SORT_RELEVANCE.value);

      await updateFromQuery({
        queryParams,
        language,
        initialLoad: true,
        // FIXME: the filtersEventCaptureData below is never used because initialLoad is true
        // However, it's required for the function signature. We should get rid of the initialLoad logic
        filtersEventCaptureData: {
          filtered_apps_in_bucket: 0,
          filters_applied_to_bucket: [],
          total_apps_in_bucket: 0,
        },
        filters: {
          // initLoad is only called once when the page is loaded, for ANZ sites where
          // must-have filter is enabled by default, we need this check to populate rrGlobalFilters
          rrGlobalFilters: self.filtersStore.selectedRrFilters ?? [],
        },
        experiments,
      });
    };

    const initLoadFilters = (questionnaire: IQuestionnaireStore) => {
      self.filtersStore.load(questionnaire);
    };

    /**
     * updateListStateAfterChangeStatus
     *
     * @description Function to put removed Job Application details into UndoState and move one Job Application up from the next page
     */
    const updateListStateAfterChangeStatus = async (
      jobApplicationId: string,
    ): Promise<void> => {
      const indexToRemove = self.jobApplications.findIndex(
        (jobApp) => jobApp.id === jobApplicationId,
      );

      if (indexToRemove < 0) {
        return;
      }

      const jobApplicationToRemove = self.jobApplications[indexToRemove];

      self.undoState = {
        previousBucket: self.bucket,
        destinationBucket: jobApplicationToRemove.bucket,
        pageNumber: self.currentPage,
        position: indexToRemove,
        jobApplication: JobApplication.create(toJS(jobApplicationToRemove)),
      };

      if (self.hasSearchText) {
        // is in search view
        return;
      }

      self.jobApplications.splice(indexToRemove, 1);
      self.incrementTotal(-1);

      // doing this in the ui atm - so that it updates the url
      // need to work out how to update the url from here
      // if (self.currentPage > 1 && self.currentPage > self.numberOfPages) {
      //   self.currentPage -= 1;
      //   await self.loadJobApplications();
      // }
    };

    const updateListStateAfterChangeStatusUndo = (): IJobApplication => {
      if (!self.undoState?.jobApplication) {
        throw new Error('No job application in undoState');
      }

      const undoJobApplication = self.undoState.jobApplication;
      const existingJobAppIndex = self.jobApplications.findIndex(
        (jobApp) => jobApp.id === undoJobApplication.id,
      );

      if (self.hasSearchText) {
        if (existingJobAppIndex >= 0) {
          return self.jobApplications[existingJobAppIndex]; // already there
        }
        return undoJobApplication;
      }

      const { previousBucket, destinationBucket, position, pageNumber } =
        self.undoState;

      if (previousBucket === self.bucket) {
        self.incrementTotal(1);
        if (pageNumber === self.currentPage) {
          self.jobApplications.splice(position, 0, undoJobApplication);
        }
      } else if (destinationBucket === self.bucket) {
        self.incrementTotal(-1);
        if (existingJobAppIndex >= 0) {
          self.jobApplications.splice(existingJobAppIndex, 1);
        }
      }

      self.updateFiltersCount(undoJobApplication, 'AddJobApp');
      self.undoState.jobApplication = undefined;
      return undoJobApplication;
    };

    const fixOutOfSyncTotal = () => {
      if (self.total < self.notRemovedJobApplications.length) {
        logError(
          {
            errorCode: 'NEGATIVE_BUCKET_COUNT',
            description: 'Job application totals are out of sync',
          },
          {
            removedJobApplicationsLength: self.removedJobApplications.length,
            notRemovedJobApplicationsLength:
              self.notRemovedJobApplications.length,
            total: self.total,
            preFilterTotal: self.preFilterTotal,
          },
        );

        const newTotal = self.notRemovedJobApplications.length;
        self.total = newTotal;
        if (self.preFilterTotal < newTotal) {
          self.preFilterTotal = newTotal;
        }
        if (self.bucket !== '') {
          const originalCount =
            self.bucketStatsStore.bucketStats.getBucketCount(
              self.bucket as BUCKETS,
            );
          self.bucketStatsStore.bucketStats.incrementBucketCount(
            self.bucket as Bucket,
            newTotal - originalCount,
          );
        }
      }
    };

    const clickViewMovedJobApplications = (): void => {
      trackViewApplicationsAfterBulkMovePressed();
      self.clearAllSelected();
    };

    return {
      updateListStateAfterChangeStatus,
      updateListStateAfterChangeStatusUndo,
      updateFromQuery,
      initLoad,
      initLoadFilters,
      showOverlay,
      hideOverlay,
      fixOutOfSyncTotal,
      clickViewMovedJobApplications,
    };
  });

export interface IJobApplicationStore
  extends Instance<typeof JobApplicationStore> {}

export default JobApplicationStore;
